// src/features/s-curve/lib/sCurveDraftEdit.ts
import type { DraftTreeIndex } from "./wbsTree";
import { formatProjectDateOnly } from 'src/utils/date';

type Range = { start: string; finish: string };

// export type DraftIndex = {
//     byKey: Map<string, any>;
//     keyToParentKey: Map<string, string | null>;
//     childrenMap: Map<string, string[]>;
// };
export type DraftIndex = DraftTreeIndex<any>;

export type DraftGlobals = {
    globalMinStart: string;
    globalMaxFinish: string;
    firstRootKey: string | null;
    lastRootKey: string | null;
};

export type ActionState = {
    canShiftBack: boolean;
    canShiftFwd: boolean;
    canShrink: boolean; // finish - 1
    canExtend: boolean; // finish + 1
    canStartBack: boolean; // start - 1 (finish 고정)
    canStartFwd: boolean; // start + 1 (finish 고정)
};

// -------------------------------------
// Date utils (SCurveContainer에서 가져옴)
// -------------------------------------
function parseISODate(s: string | null | undefined): Date | null {
    if (!s) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return null;
    const y = Number(m[1]);
    const mm = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mm, d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
}

function fmtISODate(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

export function toISODateOrNull(v: any): string | null {
    if (typeof v !== 'string') return null;
    const s = formatProjectDateOnly(v, 'Asia/Seoul', '');
    return parseISODate(s) ? s : null;
}

export function addDaysISO(s: string | null, deltaDays: number): string | null {
    const d = parseISODate(s);
    if (!d) return s;
    d.setDate(d.getDate() + deltaDays);
    return fmtISODate(d);
}

export function diffDaysISO(a: string, b: string) {
    const da = parseISODate(a)!;
    const db = parseISODate(b)!;
    return Math.round((db.getTime() - da.getTime()) / (24 * 3600 * 1000));
}

export function getRangeOf(it: any): Range | null {
    const s = toISODateOrNull(it?.plannedStart);
    const f = toISODateOrNull(it?.plannedFinish);
    if (!s || !f) return null;
    if (parseISODate(s)!.getTime() > parseISODate(f)!.getTime()) return null;
    return { start: s, finish: f };
}

function clamp(x: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, x));
}

// -------------------------------------
// Tree helpers
// -------------------------------------
export function collectDescendants(rootKey: string, childrenMap: Map<string, string[]>) {
    const out: string[] = [];
    const stack = [...(childrenMap.get(rootKey) ?? [])];
    while (stack.length) {
        const k = stack.pop()!;
        out.push(k);
        const kids = childrenMap.get(k);
        if (kids?.length) stack.push(...kids);
    }
    return out;
}

function buildPosMap(items: any[], keyOf: (it: any) => string) {
    const pos = new Map<string, number>();
    for (let i = 0; i < items.length; i++) pos.set(keyOf(items[i]), i);
    return pos;
}

// -------------------------------------
// Globals (경계/락) - 한 번만 계산해서 재사용
// -------------------------------------
export function computeDraftGlobals(items: any[], idx: DraftIndex, keyOf: (it: any) => string): DraftGlobals | null {
    const ranges = items.map(getRangeOf).filter(Boolean) as Range[];
    if (!ranges.length) return null;

    const globalMinStart = ranges.reduce((m, r) => (r.start < m ? r.start : m), ranges[0].start);
    const globalMaxFinish = ranges.reduce((m, r) => (r.finish > m ? r.finish : m), ranges[0].finish);

    const rootKeys = items.map((it) => keyOf(it)).filter((k) => (idx.keyToParentKey.get(k) ?? null) == null);

    let firstRootKey: string | null = null;
    let lastRootKey: string | null = null;

    for (const k of rootKeys) {
        const it = idx.byKey.get(k);
        const rr = getRangeOf(it);
        if (!rr) continue;

        if (!firstRootKey) firstRootKey = k;
        else {
            const cur = getRangeOf(idx.byKey.get(firstRootKey));
            if (cur && rr.start < cur.start) firstRootKey = k;
        }

        if (!lastRootKey) lastRootKey = k;
        else {
            const cur = getRangeOf(idx.byKey.get(lastRootKey));
            if (cur && rr.finish > cur.finish) lastRootKey = k;
        }
    }

    return { globalMinStart, globalMaxFinish, firstRootKey, lastRootKey };
}

function getBoundForKey(rowKey: string, idx: DraftIndex, g: DraftGlobals) {
    const parentKey = idx.keyToParentKey.get(rowKey) ?? null;
    if (parentKey) {
        const parent = idx.byKey.get(parentKey);
        const pr = getRangeOf(parent);
        if (!pr) return null;
        return { parentKey, boundStart: pr.start, boundFinish: pr.finish };
    }
    return { parentKey: null, boundStart: g.globalMinStart, boundFinish: g.globalMaxFinish };
}

// -------------------------------------
// ActionState 계산(기존 actionStateByKey 통째로 이동)
// -------------------------------------
export function computeActionStateByKey(items: any[], idx: DraftIndex, keyOf: (it: any) => string) {
    const g = computeDraftGlobals(items, idx, keyOf);
    if (!g) return new Map<string, ActionState>();

    const state = new Map<string, ActionState>();
    const off: ActionState = {
        canShiftBack: false,
        canShiftFwd: false,
        canShrink: false,
        canExtend: false,
        canStartBack: false,
        canStartFwd: false,
    };

    for (const it of items) {
        const k = keyOf(it);
        const my = getRangeOf(it);
        if (!my) {
            state.set(k, off);
            continue;
        }

        const parentKey = idx.keyToParentKey.get(k) ?? null;

        // ROOT
        if (!parentKey) {
            const isFirst = k === g.firstRootKey;
            const isLast = k === g.lastRootKey;
            const isLockedRoot = isFirst || isLast;

            const canShiftBack = !isLockedRoot && diffDaysISO(g.globalMinStart, my.start) >= 1;
            const canShiftFwd = !isLockedRoot && diffDaysISO(my.finish, g.globalMaxFinish) >= 1;

            const canExtend = !isLast && diffDaysISO(my.finish, g.globalMaxFinish) >= 1;
            const canShrink = !isLast && diffDaysISO(my.start, my.finish) >= 1;

            const canStartBack = !isFirst && diffDaysISO(g.globalMinStart, my.start) >= 1;
            const canStartFwd = !isFirst && diffDaysISO(my.start, my.finish) >= 1;

            state.set(k, { canShiftBack, canShiftFwd, canShrink, canExtend, canStartBack, canStartFwd });
            continue;
        }

        // CHILD
        const parent = idx.byKey.get(parentKey);
        const pr = getRangeOf(parent);
        if (!pr) {
            state.set(k, off);
            continue;
        }

        const canShiftBack = diffDaysISO(pr.start, my.start) >= 1;
        const canShiftFwd = diffDaysISO(my.finish, pr.finish) >= 1;

        const canExtend = diffDaysISO(my.finish, pr.finish) >= 1;
        const canShrink = diffDaysISO(my.start, my.finish) >= 1;

        const canStartBack = diffDaysISO(pr.start, my.start) >= 1;
        const canStartFwd = diffDaysISO(my.start, my.finish) >= 1;

        state.set(k, { canShiftBack, canShiftFwd, canShrink, canExtend, canStartBack, canStartFwd });
    }

    return state;
}

// -------------------------------------
// Editing ops (기존 onShift/onExtendFinish/onExtendStart 이동)
// -------------------------------------
export function applyShiftDraft(
    items: any[],
    idx: DraftIndex,
    keyOf: (it: any) => string,
    rowKey: string,
    dir: 'BACK' | 'FWD',
) {
    const me = idx.byKey.get(rowKey);
    const my = getRangeOf(me);
    if (!me || !my) return items;

    const g = computeDraftGlobals(items, idx, keyOf);
    if (!g) return items;

    const isLocked = rowKey === g.firstRootKey || rowKey === g.lastRootKey;
    if (isLocked) return items;

    const bound = getBoundForKey(rowKey, idx, g);
    if (!bound) return items;

    const want = dir === 'FWD' ? 1 : -1;

    const maxBack = -diffDaysISO(bound.boundStart, my.start);
    const maxFwd = diffDaysISO(my.finish, bound.boundFinish);

    const delta = clamp(want, maxBack, maxFwd);
    if (delta === 0) return items;

    const descendants = collectDescendants(rowKey, idx.childrenMap);
    const affected = [rowKey, ...descendants];

    const copied = items.map((x) => ({ ...x }));
    const pos = buildPosMap(copied, keyOf);

    for (const k of affected) {
        const i = pos.get(k);
        if (i == null) continue;
        copied[i].plannedStart = addDaysISO(toISODateOrNull(copied[i].plannedStart), delta);
        copied[i].plannedFinish = addDaysISO(toISODateOrNull(copied[i].plannedFinish), delta);
    }

    return copied;
}

export function applyExtendFinishDraft(
    items: any[],
    idx: DraftIndex,
    keyOf: (it: any) => string,
    rowKey: string,
    dir: 'SHRINK' | 'EXTEND',
) {
    const me = idx.byKey.get(rowKey);
    const my = getRangeOf(me);
    if (!me || !my) return items;

    const g = computeDraftGlobals(items, idx, keyOf);
    if (!g) return items;

    const isRoot = (idx.keyToParentKey.get(rowKey) ?? null) == null;
    const isLastRoot = isRoot && rowKey === g.lastRootKey;
    if (isLastRoot) return items; // lastRoot는 finish 고정

    const bound = getBoundForKey(rowKey, idx, g);
    if (!bound) return items;

    const finishDelta = dir === 'EXTEND' ? 1 : -1;
    const newFinish = addDaysISO(my.finish, finishDelta)!;

    if (newFinish < my.start) return items;
    if (newFinish > bound.boundFinish) return items;

    const P0 = my.start;
    const P1 = my.finish;
    const P1n = newFinish;

    const L = Math.max(1, diffDaysISO(P0, P1));
    const Ln = Math.max(1, diffDaysISO(P0, P1n));
    const r = Ln / L;
    const deltaL = Math.abs(Ln - L);

    const descendants = collectDescendants(rowKey, idx.childrenMap);
    const copied = items.map((x) => ({ ...x }));
    const pos = buildPosMap(copied, keyOf);

    // 1) 자기 finish 반영
    {
        const i = pos.get(rowKey);
        if (i != null) copied[i].plannedFinish = P1n;
    }

    // 2) 자손 스케일
    for (const k of descendants) {
        const i = pos.get(k);
        if (i == null) continue;

        const child = copied[i];
        const cr = getRangeOf(child);
        if (!cr) continue;

        const cLen = diffDaysISO(cr.start, cr.finish);
        if (cLen <= deltaL) continue;

        const a0 = diffDaysISO(P0, cr.start);
        const a1 = diffDaysISO(P0, cr.finish);

        const ns = addDaysISO(P0, Math.round(a0 * r));
        const nf = addDaysISO(P0, Math.round(a1 * r));

        const ns2 = ns && ns < P0 ? P0 : ns;
        const nf2 = nf && nf > P1n ? P1n : nf;

        if (!ns2 || !nf2) continue;
        if (nf2 < ns2) continue;

        child.plannedStart = ns2;
        child.plannedFinish = nf2;
    }

    return copied;
}

export function applyExtendStartDraft(
    items: any[],
    idx: DraftIndex,
    keyOf: (it: any) => string,
    rowKey: string,
    dir: 'BACK' | 'FWD',
) {
    const me = idx.byKey.get(rowKey);
    const my = getRangeOf(me);
    if (!me || !my) return items;

    const g = computeDraftGlobals(items, idx, keyOf);
    if (!g) return items;

    const isRoot = (idx.keyToParentKey.get(rowKey) ?? null) == null;
    const isFirstRoot = isRoot && rowKey === g.firstRootKey;
    if (isFirstRoot) return items; // firstRoot는 start 고정

    const bound = getBoundForKey(rowKey, idx, g);
    if (!bound) return items;

    const startDelta = dir === 'BACK' ? -1 : +1;
    const newStart = addDaysISO(my.start, startDelta)!;

    if (newStart < bound.boundStart) return items;
    if (newStart > my.finish) return items;

    const P0 = my.start;
    const P1 = my.finish;
    const P0n = newStart;

    const L = Math.max(1, diffDaysISO(P0, P1));
    const Ln = Math.max(1, diffDaysISO(P0n, P1));
    const r = Ln / L;
    const deltaL = Math.abs(Ln - L);

    const descendants = collectDescendants(rowKey, idx.childrenMap);
    const copied = items.map((x) => ({ ...x }));
    const pos = buildPosMap(copied, keyOf);

    // 1) 자기 start 반영
    {
        const i = pos.get(rowKey);
        if (i != null) copied[i].plannedStart = newStart;
    }

    // 2) 자손 스케일 (finish 고정)
    for (const k of descendants) {
        const i = pos.get(k);
        if (i == null) continue;

        const child = copied[i];
        const cr = getRangeOf(child);
        if (!cr) continue;

        const cLen = diffDaysISO(cr.start, cr.finish);

        if (cLen <= deltaL) {
            if (startDelta > 0) {
                if (cr.start < newStart) {
                    const shift = diffDaysISO(cr.start, newStart);
                    child.plannedStart = addDaysISO(cr.start, shift)!;
                    child.plannedFinish = addDaysISO(cr.finish, shift)!;
                }
            }
            continue;
        }

        const dS = diffDaysISO(cr.start, P1);
        const dF = diffDaysISO(cr.finish, P1);

        const nDS = Math.round(dS * r);
        const nDF = Math.round(dF * r);

        child.plannedStart = addDaysISO(P1, -nDS)!;
        child.plannedFinish = addDaysISO(P1, -nDF)!;
    }

    return copied;
}
